/**
 * Componente para el formulario de incidencias.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Function} props.onSubmit - Función para manejar el envío del formulario.
 * @param {Boolean} props.isUploading - Indica si hay archivos en proceso de subida.
 * @param {Number} props.uploadedFilesCount - Cantidad de archivos subidos.
 * @autor https://github.com/erabasco
 */

/**
 * HOOKS
 */
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useIncidenceTypes } from "../../hooks/useIncidenceTypes";

const IncidenceForm = ({ onSubmit, isUploading, uploadedFilesCount }) => {
    const { t } = useTranslation();
    const { types, loading: loadingTypes } = useIncidenceTypes();
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm();

    const handleFormSubmit = (data) => {
        onSubmit(data);
        reset();
    };

    // Validar si el botón debe estar deshabilitado
    const isButtonDisabled = () => {
        return isUploading || loadingTypes;
    };

    return (
        <form className="eol-form" onSubmit={handleSubmit(handleFormSubmit)}>
            {/* Type field */}
            <div className="mb-3 row align-items-center">
                <label htmlFor="type_id" className="col-sm-4 col-form-label">
                    {t("Type")}
                </label>
                <div className="col-sm-8">
                    <select
                        id="type_id"
                        name="type_id"
                        className={`form-select ${errors.type_id ? 'is-invalid' : ''}`}
                        {...register("type_id")}
                        disabled={loadingTypes}
                    >
                        <option value="">{t("Select a type")}</option>
                        {types.map((type) => (
                            <option key={type.id} value={type.id}>
                                {type.name}
                            </option>
                        ))}
                    </select>
                    {errors.type_id && (
                        <div className="text-danger mt-1">
                            {errors.type_id.message}
                        </div>
                    )}
                    {loadingTypes && (
                        <small className="form-text text-muted">
                            {t("Loading types...")}
                        </small>
                    )}
                </div>
            </div>

            {/* comment field */}
            <div className="mb-3 row align-items-center">
                <label htmlFor="comment" className="col-sm-4 col-form-label required">
                    {t("comment")}
                </label>
                <div className="col-sm-8">
                    <textarea
                        id="comment"
                        name="comment"
                        className={`form-control ${errors.comment ? 'is-invalid' : ''}`}
                        rows="5"
                        placeholder={t("Enter a detailed comment of the incidence")}
                        {...register("comment", {
                            required: t("The field is required"),
                            minLength: {
                                value: 10,
                                message: t("The comment must be at least 10 characters long")
                            },
                            maxLength: {
                                value: 2000,
                                message: t("The comment cannot exceed 2000 characters")
                            }
                        })}
                    ></textarea>
                    {errors.comment && (
                        <div className="text-danger mt-1">
                            {errors.comment.message}
                        </div>
                    )}
                    <small className="form-text text-muted">
                        {t("Provide a clear and detailed comment of the incidence")}
                    </small>
                </div>
            </div>

            {/* Submit button */}
            <div className="row">
                <div className="col-sm-8 offset-sm-4">
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isButtonDisabled()}
                    >
                        {isUploading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {t("Saving...")}
                            </>
                        ) : (
                            <>
                                <i className="bi bi-save me-2"></i>
                                {t("Save")}
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Info message about attachments */}
            {uploadedFilesCount > 0 && (
                <div className="row mt-2">
                    <div className="col-sm-8 offset-sm-4">
                        <div className="alert alert-info mb-0">
                            <i className="bi bi-info-circle me-2"></i>
                            {t("{{count}} file(s) attached", { count: uploadedFilesCount })}
                        </div>
                    </div>
                </div>
            )}
        </form>
    );
};

export default IncidenceForm;

